// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyUnit } from "@/types";
import { formatPrice, pressToOpen, rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "../selection.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    unit: TyUnit;
    onOpen: () => void;
    onActions: (anchor: DOMRect) => void;
    /** Picked by the bulk bar. Draws the ring; the screen decides what pressing the card then means. */
    isSelected?: boolean;
    /**
     * The bar is up. Every other control on the card is **gone, not disabled** — a half-live card
     * looks pressable and does something nobody asked for — and the menu button's place is taken by
     * the tick, so nothing moves and nothing overlaps.
     */
    isPicking?: boolean;
};

/**
 * One unit. A card rather than a table row: this is looked at on a phone during a site visit, and a
 * seven-column table dies at 400px.
 *
 * **The picture is a thumbnail at the start, not a cover.** A unit is identified by its number, its
 * status and its price — not by a photo of a facade that looks like every other facade in the
 * project, and most units never get a photo of their own at all. So it takes the team card's shape:
 * the picture and the identity in one row, the price under them, the figures last.
 *
 * Its own photo, or the project's when `useProjectGallery` — which is the usual case.
 */
export default function UnitCard({ unit, onOpen, onActions, isSelected, isPicking }: Props) {
    const t = useTranslation();

    const project = typeof unit.project === "object" ? unit.project : null;
    const thumb = unit.gallery?.[0] ?? (unit.useProjectGallery ? (project?.gallery?.[0] ?? null) : null);
    const building = project?.buildings?.find((entry) => entry._id === unit.building) ?? null;

    /** A rent price means nothing without the period it covers — `formatPrice` carries that rule. */
    const price = formatPrice(unit, t);

    return (
        <article
            className={`unit-card pick${isSelected ? " pick--on" : ""}${isPicking ? " pick--picking" : ""}`}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={isPicking ? undefined : rightClickToOpen(onActions)}
        >
            <div className="unit-card__row">
                <span className="unit-card__thumb">
                    {thumb ? <img className="unit-card__image" src={thumb} alt="" loading="lazy" /> : <Icon name="properties" size={20} />}
                </span>

                <div className="unit-card__identity">
                    <span className="unit-card__number">{unit.number}</span>
                    <span className="unit-card__meta">
                        {unit.type ? t.properties.unitTypes[unit.type] : t.properties.notSet}
                        {building ? ` · ${t.properties.fields.building} ${building.name}` : ""}
                        {unit.floor !== null ? ` · ${t.properties.fields.floor} ${unit.floor}` : ""}
                    </span>
                </div>

                {/* The one place on the card that is **not** the card: it opens a menu instead —
                    until the bar is up, when the card means one thing and this is the tick. */}
                {isPicking ? (
                    <span className={`pick__box${isSelected ? " pick__box--on" : ""}`} aria-hidden="true">
                        <Icon name="check" size={18} />
                    </span>
                ) : (
                    <button
                        type="button"
                        className="unit-card__more"
                        onClick={(event) => {
                            event.stopPropagation();
                            onActions(event.currentTarget.getBoundingClientRect());
                        }}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={18} />
                    </button>
                )}
            </div>

            <span className="unit-card__price">{price}</span>

            {/* The status, then the three facts a buyer asks for first — and only when they are known. */}
            <div className="unit-card__facts">
                <StatusChip size="sm" tone={unit.status} label={t.properties.statuses[unit.status]} />
                {unit.incomplete && <StatusChip size="sm" tone="reserved" label={t.properties.incomplete} />}

                {unit.counts.bedrooms !== null && (
                    <span className="unit-card__fact">
                        {unit.counts.bedrooms} {t.properties.counts.bedrooms}
                    </span>
                )}
                {unit.counts.bathrooms !== null && (
                    <span className="unit-card__fact">
                        {unit.counts.bathrooms} {t.properties.counts.bathrooms}
                    </span>
                )}
                {unit.measures.builtArea !== null && (
                    <span className="unit-card__fact">
                        {unit.measures.builtArea} {t.properties.sqm}
                    </span>
                )}
            </div>
        </article>
    );
}
